<template>
  <div class="">
    <div>
      <div v-if="isDataLoading" class="d-flex spinner-container flex-column justify-content-center align-items-center gap-3 py-3">
        <b-spinner class="spinner" variant="primary" label="Text Centered"></b-spinner>
        <p class="mb-0 text-primary fw-bold">Loading data, please wait...</p>
      </div>
      <div v-if="attemptedAppsForExam" class="pt-3 pt-sm-0">
        <div v-if="attemptedAppsForExam?.length > 0" class="row">
          <div class="col-lg-12">
            <section class="d-flex flex-column align-items-center">
              <h1 class="text-center text-primary heading">{{ this.user_name }}&apos;s Mock Performance Analytics</h1>
              <p class="text-center fw-medium text-dark">Track your progress, identify weakness and conquer {{ interesName?.toUpperCase()}}</p>
              <select class="py-2 px-1 pe-2" v-model="selectedApp" @change="getMockData">
                <option :value="null">All Mocks</option>
                <option v-for="(data, index) in attemptedAppsForExam" :key="index" :value="data?.app_id">
                  {{ data?.subject_module_name }}
                </option>
              </select>
            </section>
            <div class="px-2 px-sm-4 py-3 w-100">
              <!-- attempt-wise module performance -->
              <section v-if="stackedColumnSeries.length > 0 && VUE_APP_ENV != 'prod'" class="pb-5 pt-3">
                <div class="d-flex align-items-center justify-content-between gap-2 flex-wrap mb-2">
                  <div class="d-flex align-items-center gap-2 text-primary fs-3 fw-bold">
                    <i class="fa-solid fa-layer-group mr-2 font-size-18"></i>
                    <p class="m-0">Attempt-wise Module Performance</p>
                  </div>
                  <p class="mb-0 fw-bold text-dark">Attempts: <span class="text-primary">{{ stackedColumnAttemptsCount }}</span></p>
                </div>
                <b-row class="gy-4">
                  <b-col cols="12">
                    <div class="box">
                      <div id="chart" class="chart-scroll-x">
                        <apexchart type="line" height="380" :options="stackedColumnOptions" :series="stackedColumnSeries"></apexchart>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </section>
              <!-- attempt-wise module performance -->
              <!-- overall analytics -->
              <section class="pb-5">
                <div class="d-flex align-items-center gap-2 text-primary fs-3 fw-bold">
                  <i class="fa-solid fa-arrow-trend-up mr-2 font-size-18"></i>
                  <p class="m-0"> {{ selectedAppName ? `${selectedAppName} Analytics` : "Overall Analytics"}}&nbsp;{{ attemptLabel }}</p>
                </div>
                <div class="col-12">
                  <p><small v-if="!attemptLabel" class="ms-5">Analytics displayed using the latest attempt of all the attempted mocks</small></p>
                </div>
                <b-row class="d-flex align-items-stretch justify-content-center">
                  <b-col class="mb-4" :title="selectedApp? 'Score obtained in selected mock' : 'Score obtained in each mock'" cols="12" md="6" lg="4" xl="3">
                    <div class="box">
                      <div id="chart">
                        <apexchart type="line" height="350" :options="performanceTrendOptions" :series="performanceTrendScoreSeries"></apexchart>
                      </div>
                    </div>
                  </b-col>
                  <b-col class="mb-4" cols="12" md="6" lg="4" xl="3">
                    <div class="box">
                      <div class="d-flex align-items-center justify-content-between gap-3">
                        <h3 class="box-heading">Goal Progress</h3>
                        <i class="fa fa-info-circle font-size-18 pointer pe-2" :title="selectedApp ? 'Highest score achieved in the selected mock' : 'Highest score achieved across all mocks'"></i>
                      </div>
                      <div style="height: 90%;" class="d-flex flex-column justify-content-center overflow-hidden">
                        <div class="d-flex flex-column align-items-center w-100 mb-2">
                          <p style="font-size: 18px; line-height: 16px;" class="mb-0 text-dark">{{selectedApp ? 'Current Score' : 'Highest Score'}}</p>
                          <p class="mb-0 fw-bolder fs-5 text-dark">
                            <span class="fw-bolder fs-1 text-primary">{{ goal_progress.goalProgressScore }}</span>/{{ goal_progress.goalProgressTotalScore }}<br/>
                          </p>
                          <p v-if="goal_progress.composite_score">
                            <span class="fw-bolder fs-1 text-primary">Composite Score:&nbsp;{{ goal_progress.composite_score }}</span>
                          </p>
                        </div>
                        <div
                          style="height: 20px; max-width: 250px;"
                          class="progress mb-3 mx-auto w-100"
                        >
                          <div
                            class="progress-bar bg-primary"
                            role="progressbar"
                            :style="{ width: calculatedWidth(goal_progress.goalProgressScore, goal_progress.goalProgressTotalScore) }"
                            :aria-valuenow="goal_progress.goalProgressScore"
                            aria-valuemin="0"
                            :aria-valuemax="goal_progress.goalProgressTotalScore"
                          ></div>
                        </div>
                        <p style="font-size: 14px; line-height: 16px;" class="mb-0 text-dark text-center">Goal: {{ goal_progress.goalProgressTotalScore }}&nbsp;<span class="text-dark">({{ parseFloat((goal_progress.goalProgressScore / goal_progress.goalProgressTotalScore * 100).toFixed(2)) }}% achived)</span></p>
                      </div>
                    </div>
                  </b-col>
                  <b-col class="mb-4" cols="12" md="6" lg="4" xl="3">
                    <div class="box">
                      <div class="d-flex align-items-center justify-content-between gap-3">
                        <h3 class="box-heading">{{ selectedAppName ? `Accuracy` : "Overall Accuracy"}}</h3>
                        <i class="fa fa-info-circle font-size-18 pointer" aria-hidden="true"  :title="selectedApp ? 'Percentage of correct answers in the selected mock' : 'Average of percentage of correct answers across all mocks'"></i>
                      </div>
                      <div style="height: 90%;" class="d-flex flex-column justify-content-center overflow-hidden" id="chart">
                        <apexchart type="radialBar" height="250" :options="accuracyChartOptions" :series="accuracySeries"></apexchart>
                      </div>
                    </div>
                  </b-col>
                  <b-col class="mb-4" cols="12" md="6" lg="4" xl="3">
                    <!-- Dont Show as this needs to be evaluated and deprecated -->
                    <div class="box" v-if="false">
                      <div class="d-flex align-items-center justify-content-between gap-3">
                        <h3 class="box-heading">Avg. Time/Ques.</h3>
                        <i class="fa fa-info-circle font-size-18 pointer pe-2" :title="selectedApp ? 'Average time taken per question in the selected mock' : 'Average time taken per question across all mocks'"></i>
                      </div>
                      <div style="height: 90%;" class="d-flex flex-column justify-content-center align-items-center overflow-hidden">
                        <span class="fw-bolder fs-1 text-primary">{{ avg_speed | convertSecondsToMinutesAndSeconds}}</span>
                      </div>
                    </div>
                  </b-col>
                </b-row>
                <b-row v-if="VUE_APP_ENV != 'prod'" class="d-flex align-items-stretch justify-content-center">
                  <!-- Target readiness -->
                  <b-col class="mb-4" cols="12" md="6" lg="4">
                    <div class="box d-flex flex-column justify-content-between">
                      <div class="d-flex align-items-center justify-content-between gap-3">
                        <h3 class="box-heading mb-0">Target Readiness</h3>
                        <!-- <small class="text-muted font-italic font-size-12">updated 2h ago</small> -->
                      </div>
                      <div class="d-flex flex-column justify-content-center align-items-center flex-grow-1 py-2">
                        <div class="w-100 d-flex justify-content-center">
                          <apexchart
                            type="radialBar"
                            height="230"
                            :options="readinessOptions"
                            :series="readinessSeries"
                          ></apexchart>
                        </div>
                        <p class="readiness-text text-center text-muted mb-0">
                          On pace for a 1500+ November attempt<br>if the current streak holds.
                        </p>
                      </div>
                    </div>
                  </b-col>

                  <!-- Topic accuracy -->
                  <b-col class="mb-4" cols="12" md="6" lg="4">
                    <div class="box d-flex flex-column">
                      <div class="d-flex align-items-center justify-content-between gap-3 mb-2">
                        <h3 class="box-heading mb-0">Topic Accuracy</h3>
                        <!-- <small class="text-muted font-italic font-size-12">last 500 questions</small> -->
                      </div>
                      <div class="topic-accuracy-container flex-grow-1">
                        <div class="topic-accuracy-scroll">
                          <div
                            v-for="topic in topicAccuracies"
                            :key="topic.name"
                            class="g-bar-item"
                            :class="{ weak: topic.pct < 70 }"
                          >
                            <div class="d-flex justify-content-between align-items-center mb-1">
                              <span class="font-size-13 fw-semibold text-dark text-truncate me-2">{{ topic.name }}</span>
                              <b class="font-monospace font-size-13 fw-bold topic-pct">{{ topic.pct }}%</b>
                            </div>
                            <div class="g-progress-track">
                              <div
                                class="g-progress-fill"
                                :style="{ width: topic.pct + '%' }"
                              ></div>
                            </div>
                          </div>
                          <div v-if="topicAccuracies.length == 0" class="text-center text-muted p-3 d-flex justify-content-center align-items-center h-100 w-100">
                            <p class="mb-0">No topic accuracy data available</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-col>

                  <!-- Focus list -->
                  <b-col class="mb-4" cols="12" md="6" lg="4">
                    <div class="box d-flex flex-column">
                      <div class="d-flex align-items-center justify-content-between gap-3 mb-2">
                        <h3 class="box-heading mb-0">Focus List</h3>
                        <!-- <small class="text-muted font-italic font-size-12">this week</small> -->
                      </div>
                      <div class="focus-strengths-container flex-grow-1">
                        <div v-if="focusList.length > 0" class="g-chips mb-3">
                          <span
                            v-for="item in focusList"
                            :key="item"
                            class="g-chip chip-weak"
                          >{{ item }}</span>
                        </div>
                        <div v-else class="text-center text-muted p-3">
                          <p class="mb-0">No focus areas identified</p>
                        </div>
                        <div class="d-flex align-items-center justify-content-between gap-3 mb-2">
                          <h3 class="box-heading mb-0 font-size-18">Strengths</h3>
                          <!-- <small class="text-muted font-italic font-size-12">protect these</small> -->
                        </div>
                        <div v-if="strengthsList.length > 0" class="g-chips">
                          <span
                            v-for="item in strengthsList"
                            :key="item"
                            class="g-chip chip-good"
                          >{{ item }}</span>
                        </div>
                        <div v-else class="text-center text-muted p-3">
                          <p class="mb-0">Keep practicing to discover your strengths</p>
                        </div>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </section>
              <!-- overall analytics -->
              <!-- subject-wise breakdown-->
              <section v-if="Object.keys(subject_wise_best_performance).length > 0" class="pb-5 pt-3">
                <div class="d-flex align-items-center gap-2 text-primary fs-3 fw-bold mb-3">
                 <i class="fa fa-file-text mr-2 font-size-18" aria-hidden="true"></i>
                  <p class="m-0">Subject Wise Breakdown</p>
                </div>
                <div class="d-flex h-auto gap-4 flex-wrap">
                  <div class="p-2 mb-4 rounded-2 subject-wise-box bg-white" v-for="(value, key) in subject_wise_best_performance" :key="key">
                    <b class="fs-5 fw-bold mb-3">{{ fixTestName(value.app_name) }}</b>
                    <div class="mb-2">
                      <div class="d-flex align-items-center justify-content-between h-27">
                        <span>Accuracy</span>
                        <span class="fw-bold">{{value.accuracy}}%</span>
                      </div>
                      <div style="height: 20px;" class="progress mb-3 w-100">
                        <div
                          class="progress-bar bg-primary"
                          role="progressbar"
                          :style="{ width: calculatedWidth(value.accuracy, 100) }"
                          :aria-valuenow="value.accuracy"
                          aria-valuemin="0"
                          :aria-valuemax="100"
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div class="d-flex justify-content-between h-27">
                        <span>Time&nbsp;/&nbsp;Ques</span>
                        <span class="fw-bold">{{ (1/value.speed) | convertSecondsToMinutesAndSeconds }}</span>
                      </div>
                      <div style="height: 20px;" class="progress mb-1 w-100">
                        <div
                          class="progress-bar bg-primary"
                          role="progressbar"
                          :style="{ width: calculatedWidth(value.speed, value.max_val_for_progress_bar) }"
                          :aria-valuenow="value.speed"
                          aria-valuemin="0"
                          :aria-valuemax="value.max_val_for_progress_bar"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </section><!-- subject-wise breakdown-->

              <!-- exam activity -->
              <section v-if="!selectedApp" class="pb-5 pt-3">
                <div class="d-flex align-items-center gap-2 text-primary fs-3 fw-bold mb-3">
                  <i class="fa fa-bar-chart mr-2 font-size-18" aria-hidden="true"></i>
                  <p class="m-0">Exam Activity</p>
                </div>
                <div class="box mx-auto col-12 col-md-6 mb-4">
                  <div id="chart">
                    <apexchart type="bar" height="380" :options="examChartOptions" :series="examSeries"></apexchart>
                  </div>
                </div>
              </section>
              <!-- exam activity -->

              <!-- actionable recommendations -->
              <section v-if="selectedApp" class="pb-5 pt-3">
                <div class="d-flex align-items-center gap-2 text-primary fs-3 fw-bold mb-3">
                  <i class="fa-solid fa-lightbulb mr-2 font-size-18"></i>
                  <p class="m-0">Actionable Recommendations</p>
                </div>
                <div class="card bg-white p-3 col-12 col-lg-12 insights-border rounded-2">
                  <div v-if="resultInsights && resultInsights?.length && !resultInsights?.every(item => item.result_insights === null)">
                      <div v-for="(chainItem, i) in resultInsights" :key="i">
                        <div v-if="chainItem.result_insights">
                          <div class="pt-2">
                            <h4>{{chainItem.app_name_alias}}</h4>
                            <ul class="pt-0 ms-2">
                              <li class="pt-1 ps-0" v-for="(insgtCatList, categoryNamekey) in chainItem.result_insights" :key="categoryNamekey">
                                <b class="primary-color">{{categoryNamekey}}</b>
                                <ol class="pt-0 ms-1">
                                  <li v-for="(insgt, j) of insgtCatList" :key="j">
                                    <p class="">{{insgt}}</p>
                                  </li>
                                </ol>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                  </div>
                  <div class="py-3">
                    <div v-if="isLoadingInsights" class="d-flex flex-column justify-content-center align-items-center gap-3">
                      <b-spinner variant="primary" label="Text Centered"></b-spinner>
                      <p class="mb-0 text-primary fw-bold">Generating your result insights, please wait...</p>
                    </div>
                    <div v-else class="d-flex justify-content-center align-items-center gap-3 flex-wrap">
                      <b-button :disabled="isLoadingInsights" v-if="(!isLoadingInsights && (!resultInsights || resultInsights.length === 0)) || (!isLoadingInsights && resultInsights?.every(item => item.result_insights === null))" class="btn btn-primary m-3" id="gptBtn" @click="generateResultInsights">
                        Generate Insights
                      </b-button>
                      <b-button :disabled="isLoadingInsights" v-if="resultInsights?.some(item => item.result_insights === null) && !resultInsights?.every(item => item.result_insights === null) && !isLoadingInsights" class="btn btn-primary m-3" id="gptBtn" @click="generateResultInsights">
                        Generate Full Insights
                      </b-button>
                    </div>
                  </div>
                </div>
              </section>
              <!-- actionable recommendations -->
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Toast -->
    <div v-if="showToast" class="position-fixed top-0 end-0 p-3" style="z-index: 2000;">
      <alert
          :variant="toastVariant"
          :isdismissable="true"
          v-if="showToast"
          className="fade show rounded-0 alert-solid shadow mb-3"
        >
          <template v-slot>
            <span class="d-flex align-items-center">
              {{ toastMsg }}
            </span>
          </template>
      </alert>
    </div><!-- Toast -->
  </div>
</template>

<script>
import { TprepAnalytics } from "../../../FackApi/api/TprepAnalytics.js"
import { socialvue } from "../../../config/pluginInit"
import { reorderObjectBasedOnSetConfig } from "../../../Utils/appChainOrder.js"
import VueApexCharts from "vue-apexcharts"
import { User } from "../../../FackApi/api/user.js"

export default {
  name: "Analytics",
  components: {
    apexchart: VueApexCharts
  },
  data () {
    return {
      // toast
      gideUserId: null,
      aprsId: null,
      attempt: null,
      examName: null,
      showToast: false,
      toastMsg: "",
      toastVariant: "default",
      primaryColor: null,
      VUE_APP_ENV: process.env.VUE_APP_ENV,
      // performance
      performanceTrendScoreSeries: [{
        name: "Score",
        data: []
      }],
      performanceTrendOptions: {
        chart: {
          height: 350,
          type: "line",
          zoom: {
            enabled: false
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: "straight"
        },
        title: {
          text: "Performance Trend",
          align: "left",
          style: {
            fontSize: "16px",
            fontWeight: "bold"
          }
        },
        grid: {
          row: {
            colors: ["#f3f3f3", "transparent"],
            opacity: 0.5
          }
        },
        xaxis: {
          categories: []
        }
      },
      // attempt-wise module performance (stacked column)
      stackedColumnSeries: [],
      stackedColumnAttemptsCount: 0,
      stackedColumnOptions: {
        chart: {
          type: "bar",
          height: 380,
          stacked: true,
          zoom: { enabled: false },
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          }
        },
        plotOptions: {
          bar: {
            columnWidth: "30%",
            dataLabels: {
              total: {
                enabled: true,
                style: {
                  fontSize: "13px",
                  fontWeight: 700
                }
              }
            }
          }
        },
        title: {
          text: "Attempt-wise Module Performance",
          align: "left",
          style: {
            fontSize: "16px",
            fontWeight: "bold"
          }
        },
        xaxis: {
          categories: []
        },
        legend: {
          position: "bottom"
        }
      },
      accuracySeries: [],
      accuracyChartOptions: {
        chart: {
          height: 250,
          type: "radialBar"
        },
        plotOptions: {
          radialBar: {
            hollow: {
              size: "70%"
            }
          }
        },
        labels: ["Accuracy"]
      },
      readinessSeries: [0],
      readinessOptions: {
        chart: {
          type: "radialBar",
          height: 230,
          sparkline: { enabled: true }
        },
        colors: ["#e5252c"],
        plotOptions: {
          radialBar: {
            hollow: { size: "62%" },
            track: {
              background: "#f1f1f1",
              strokeWidth: "100%"
            },
            dataLabels: {
              name: {
                show: true,
                fontSize: "12px",
                color: "#6c757d",
                offsetY: -8
              },
              value: {
                show: true,
                fontSize: "24px",
                fontWeight: "800",
                color: "#212529",
                offsetY: 4,
                formatter: function (val) {
                  return `${val}%`
                }
              }
            }
          }
        },
        labels: ["READY"]
      },
      topicAccuracies: [
        // { name: "Algebra & functions", pct: 94 },
        // { name: "Reading — inference", pct: 89 },
        // { name: "Grammar & usage", pct: 91 },
        // { name: "Data analysis", pct: 68 },
        // { name: "Geometry & trig", pct: 61 }
      ],
      // focusList: ["Circles & arcs", "Box plots", "Pacing: module 2"],
      // strengthsList: ["Linear systems", "Vocabulary in context", "Punctuation"],
      focusList: [],
      strengthsList: [],
      examSeries: [{
        name: "Mocks",
        data: []
      }],
      examChartOptions: {
        chart: {
          type: "bar",
          height: 380
        },
        xaxis: {
          categories: []
        },
        title: {
          text: "Mock Exams Taken",
          align: "left",
          style: {
            fontSize: "22px",
            fontWeight: "bold"
          }
        }
      },
      attemptedAppsForExam: null,
      selectedApp: null,
      goal_progress: {},
      avg_speed: 0,
      subject_wise_best_performance: {},
      resultInsights: null,
      selectedAppName: null,
      isLoadingInsights: false,
      noOfAppsInChain: 0,
      isDataLoading: false,
      interestName: null,
      interestId: null,
      resultOwnerDetails: null,
      user_name: null,
      attemptLabel: null
    }
  },
  computed: {
    userData () {
      return this.$store.getters["User/userData"]
    }
  },
  async mounted () {
    // ALL MOCKS http://localhost:14433/analytics?gide_user_id=b48b3ac36922f70fa9bd829d927e430d&interest_id=TESTSERIES_SAT_MOCK
    // SPECIFIC MOCKS http://localhost:14433/analytics?app_id=APPc16fefe70fca43078aecf3a5df0ac5d7&gide_user_id=b48b3ac36922f70fa9bd829d927e430d&interest_id=TESTSERIES_SAT_MOCK&aprs_id=APRS979404c60440d5232ede4b163331d4b5&attempt=1

    socialvue.index()
    this.selectedApp = this.$route.query.app_id
    this.gideUserId = this.$route.query.gide_user_id ? this.$route.query.gide_user_id : this.userData.user_id
    this.interestId = this.$route.query.interest_id
    this.aprsId = this.$route.query.aprs_id
    this.attempt = this.$route.query.attempt ? this.$route.query.attempt : null

    const routeInterest = this.interestId?.toLowerCase() || ""
    if (routeInterest.includes("sat")) {
      this.interesName = "sat"
    }
    else if (routeInterest.includes("cuet")) {
      this.interesName = "cuet"
    }
    else if (routeInterest.includes("ap")) {
      this.interesName = "ap"
    }
    else if (routeInterest.includes("clat")) {
      this.interesName = "clat"
    }
    else {
      this.interesName = routeInterest // fallback
    }

    let userViewResp = await User.userView(this, this.gideUserId, this.userData.user_id)
    if (userViewResp.resp_status) {
      this.resultOwnerDetails = userViewResp.resp_data.data
      this.user_name = this.resultOwnerDetails.user_name ? this.resultOwnerDetails.user_name : this.resultOwnerDetails.user_email
    }

    this.loadRequiredData()
  },
  methods: {
    /**
     * load Required Data
     */
    loadRequiredData () {
      this.setPrimaryColor()
      this.getTprepAnalyticsAttemptedAppsForExam()
      this.getMockData()
    },
    /**
     * getInsights
     */
    async getInsights () {
      try {
        const resultInsights = await TprepAnalytics.tprep_analyticsAttemptedMocksResultAnalysis(this, { app_id: this.selectedApp, gide_user_id: this.gideUserId })
        if (Array.isArray(resultInsights.resp_data.data)) {
          // decoding result_insights
          this.resultInsights = resultInsights.resp_data.data.map(app => ({ ...app, result_insights: app.result_insights ? JSON.parse(decodeURIComponent(app.result_insights)) : null }))
          this.noOfAppsInChain = this.resultInsights.length
        }
        else {
          this.resultInsights = null
        }
      }
      catch (err) {
        console.error("Exception in getInsights() and error: ", err.messasge)
      }
    },
    /**
    * getMockData
    */
    async getMockData () {
      if (this.selectedApp) {
        await this.getInsights()

        let payload = {}
        let appData = this.attemptedAppsForExam?.find(e => e.app_chain.includes(this.selectedApp))
        if (!this.aprsId && !this.attempt) {
          // If attempt no is not found then get the latest attempt
          payload = {
            gide_user_id: this.gideUserId,
            subject_module_name: appData?.subject_module_name,
            app_id: appData?.app_id,
            attempt: appData?.latest_attempt_no,
            aprs_id: appData?.aprs_id
          }
        }
        else {
          payload = {
            gide_user_id: this.gideUserId,
            subject_module_name: appData?.subject_module_name,
            app_id: this.selectedApp,
            attempt: this.attempt,
            aprs_id: this.aprsId
          }
        }

        this.getTprepAnalyticsPerformanceTrend(payload)
        this.getTprepAnalyticsStackedColumnData(this.selectedApp)
        this.getAnalytics({
          app_id: this.selectedApp,
          gide_user_id: this.gideUserId,
          // attempt: this.attempt || appData?.latest_attempt_no,
          // aprs_id: this.aprsId || appData?.aprs_id,
          interest_id: this.interestId
        })

        // getting selected mock name
        this.selectedAppName = appData?.subject_module_name.split(":")[0]
        this.attemptLabel = `[Attempt: ${this.attempt}]`
      }
      else {
        // overall mock
        this.attemptLabel = null
        this.resultInsights = null
        this.selectedAppName = null
        this.selectedApp = null
        this.getTprepAnalyticsPerformanceTrend()
        this.getMonthWiseMocksTakenCount()
        this.getTprepAnalyticsStackedColumnData(null)
        this.getAnalytics({
          interest_id: this.interestId,
          gide_user_id: this.gideUserId
        })
      }
    },
    /**
     * getTprepAnalyticsStackedColumnData
     * fetches the attempt-wise, module-wise score breakdown of the selected mock (chained or single quiz app).
     * when appId is null/undefined, fetches the attempt-wise breakdown across every mock attempted for this exam ("All Mocks")
     */
    async getTprepAnalyticsStackedColumnData (appId) {
      if (this.VUE_APP_ENV == "prod") {
        return
      }

      try {
        const payload = {
          gide_user_id: this.gideUserId,
          tprep_mock_interest_id: this.interestId,
          app_id: appId
        }

        const stackedColumnData = await TprepAnalytics.tprep_analyticsStackedColumnData(this, payload)
        if (stackedColumnData.resp_status) {
          const series = stackedColumnData.series || []
          // keep the module/subject order consistent with the rest of the page (e.g. English before Maths)
          series.sort((a, b) => this.moduleSortRank(a.name) - this.moduleSortRank(b.name))

          const defaultPalette = ["#008FFB", "#00E396", "#FEB019", "#FF4560", "#775DD0", "#3F51B5", "#03A9F4", "#546E7A", "#D4526E", "#8D5B4C"]
          const chartColors = series.length === 1 ? [this.primaryColor] : series.map((_, idx) => defaultPalette[idx % defaultPalette.length])

          // total score per attempt, used to draw a trend line above the stacked columns
          let categories = stackedColumnData.categories || []
          // AP (MCQ + FRQ): a 0 score draws no segment, so print the section-wise split under each bar
          const isApSectionSplit = series.length > 0 && series.every(s => ["MCQ", "FRQ"].includes(s.name))
          if (isApSectionSplit) {
            categories = categories.map((category, idx) => [
              ...(Array.isArray(category) ? category : [category]),
              series.map(s => `${s.name}: ${Number(s.data?.[idx]) || 0}`).join(" | ")
            ])
          }
          const totalScoreTrend = categories.map((_, idx) => series.reduce((sum, s) => sum + (Number(s.data?.[idx]) || 0), 0))
          // keep the stacked columns as columns; only the trend series should render as a line
          const columnSeries = series.map(s => ({ ...s, type: "column" }))
          const trendSeries = {
            name: "Total Score Trend",
            type: "line",
            data: totalScoreTrend
          }

          const shortName = name => (name && name.includes(":") ? name.split(":")[0] : name)
          const allColors = [...chartColors, "#1F2937"]
          const legendColorByLabel = {}
          columnSeries.concat(trendSeries).forEach((s, idx) => {
            const label = shortName(s.name)
            if (!(label in legendColorByLabel)) {
              legendColorByLabel[label] = allColors[idx]
            }
          })
          const legendLabels = Object.keys(legendColorByLabel)
          const chartWidth = categories.length > 12 ? categories.length * 100 : "100%"
          const tooManySeriesForLabels = columnSeries.length > 20

          this.stackedColumnOptions = {
            ...this.stackedColumnOptions,
            chart: { ...this.stackedColumnOptions.chart, type: "line", width: chartWidth },
            xaxis: {
              categories,
              crosshairs: { show: false },
              tooltip: { enabled: false }
            },
            colors: allColors,
            legend: {
              position: "bottom",
              customLegendItems: legendLabels,
              markers: { fillColors: legendLabels.map(label => legendColorByLabel[label]) },
              onItemClick: { toggleDataSeries: false },
              onItemHover: { highlightDataSeries: false }
            },
            dataLabels: tooManySeriesForLabels
              ? { enabled: false }
              : {
                enabled: true,
                enabledOnSeries: columnSeries.map((_, idx) => idx),
                formatter (val, opts) {
                  const w = opts.w
                  const pxPerUnit = w.globals.gridHeight / (w.globals.maxY - w.globals.minY)
                  return val * pxPerUnit >= 16 ? val : ""
                }
              },
            // hovering a single segment shows only that segment's value, not the whole stack
            tooltip: {
              shared: false,
              intersect: true
            },
            stroke: {
              width: [...series.map(() => 0), 3],
              curve: "smooth"
            },
            markers: {
              size: [...series.map(() => 0), 5],
              hover: { size: 6 }
            }
          }
          this.stackedColumnSeries = [...columnSeries, trendSeries]
          this.stackedColumnAttemptsCount = stackedColumnData.attempts_count || 0
        }
        else {
          this.stackedColumnSeries = []
          this.stackedColumnAttemptsCount = 0
        }
      }
      catch (err) {
        console.error("Exception in getTprepAnalyticsStackedColumnData() and err: ", err.message)
      }
    },

    moduleSortRank (name) {
      const normalized = (name || "").toLowerCase()
      const subjectRank = (normalized.includes("reading") || normalized.includes("english") || normalized.includes("eng")) ? 0
        : normalized.includes("math") ? 1
          : 2
      const moduleNo = Number(normalized.match(/module-?\s*(\d+)/)?.[1]) || 0
      return subjectRank * 10 + moduleNo
    },
    /**
     * getAnalytics - fetches analytics data for readiness, topic accuracy, and focus & strengths data.
     */
    async getAnalytics (filterPayload = null) {
      if (process.env.VUE_APP_ENV === "prod") {
        return
      }

      try {
        const payload = {
          gide_user_id: this.gideUserId,
          ...filterPayload
        }
        if (!payload.interest_id && !payload.app_id) {
          payload.interest_id = this.interestId
        }

        const analyticsResp = await TprepAnalytics.tprepAnalyticsGetReadynessAccuracyFocusStrength(this, payload)
        if (analyticsResp && analyticsResp.resp_status && analyticsResp.resp_data) {
          const respData = analyticsResp.resp_data

          // 1. Readyness
          if (respData.readyness && respData.readyness.percentage !== undefined && respData.readyness.percentage !== null) {
            this.readinessSeries = [respData.readyness.percentage]
          }
          else {
            this.readinessSeries = [0]
          }

          // 2. Topic Accuracy
          if (Array.isArray(respData.topic_accuracy)) {
            this.topicAccuracies = respData.topic_accuracy.map(item => ({
              name: item.topic,
              pct: item.accuracy,
              total_questions: item.total_questions,
              correct_questions: item.correct_questions,
              wrong_questions: item.wrong_questions,
              total_weightage: item.total_weightage,
              obtained_score: item.obtained_score
            }))
          }
          else {
            this.topicAccuracies = []
          }

          // 3. Focus & Strengths
          if (respData.focus_strength) {
            this.focusList = Array.isArray(respData.focus_strength.focus_list) ? respData.focus_strength.focus_list : []
            this.strengthsList = Array.isArray(respData.focus_strength.strengths) ? respData.focus_strength.strengths : []
          }
          else {
            this.focusList = []
            this.strengthsList = []
          }
        }
      }
      catch (err) {
        console.error("Exception in getAnalytics() and err: ", err.message)
      }
    },
    /**
     * generateResultInsights
     */
    async generateResultInsights () {
      this.isLoadingInsights = true
      try {
        let payload = {
          app_id: this.selectedApp,
          attempt: 1,
          other_user_id: this.gideUserId
        }

        const resp = await TprepAnalytics.tprepAnalyticsResultInsightsGenerate(this, payload)
        if (resp.resp_status) {
          setTimeout(() => {
            this.getInsights()
            this.isLoadingInsights = false
          }, 2000 * this.noOfAppsInChain ?? 1)
        }
        else {
          this.isLoadingInsights = false
          this.toastMsg = "Failed to generate insights, please try again later."
          this.toastVariant = "danger"
          this.showToast = true
        }
      }
      catch (err) {
        console.error("Exception in generateResultInsights() and error: ", err.messasge)
        this.isLoadingInsights = false
        this.toastMsg = "Failed to generate insights, please try again later."
        this.toastVariant = "danger"
        this.showToast = true
      }
    },
    /**
     * calculatedWidth
     */
    calculatedWidth (currVal, maxVal) {
      const percentage = (currVal / maxVal) * 100
      return `${percentage}%`
    },
    /**
     * getMonthWiseMocksTakenCountget_active_microapp
     */
    async getMonthWiseMocksTakenCount () {
      try {
        const payload = {
          gide_user_id: this.gideUserId,
          tprep_mock_interest_id: this.interestId
        }

        const monthwiseMocksTaken = await TprepAnalytics.tprep_analyticsMonthWiseMocksTaken(this, payload)
        if (monthwiseMocksTaken.resp_status) {
          const xaxisPoints = []
          const yaxisPoints = []

          for (const data of monthwiseMocksTaken.resp_data.data) {
            xaxisPoints.push(data.month_name)
            yaxisPoints.push(data.total_mocks)
          }

          this.examChartOptions = {
            ...this.examChartOptions,
            xaxis: { categories: xaxisPoints }
          }
          this.examSeries[0].data = yaxisPoints
        }
      }
      catch (err) {
        console.error("Exception in getMonthWiseMocksTakenCount() and err: ", err.message)
      }
    },
    /**
     * getTprepAnalyticsPerformanceTrend
     */
    async getTprepAnalyticsPerformanceTrend (whereFilter = null) {
      try {
        this.isDataLoading = true
        const payload = {
          gide_user_id: this.gideUserId,
          tprep_mock_interest_id: this.interestId
        }

        if (whereFilter) {
          payload.whereFilter = whereFilter
          payload.whereFilter.subject_module_name = this.interestId
        }

        const tprepAnalyticsPerfTrendData = await TprepAnalytics.tprep_analyticsPerformanceTrend(this, payload)
        if (tprepAnalyticsPerfTrendData.resp_status) {
          this.performanceTrendOptions = { ...this.performanceTrendOptions, xaxis: { categories: Object.keys(tprepAnalyticsPerfTrendData.performance_trend_graph_points) } }
          this.performanceTrendScoreSeries[0].data = Object.values(tprepAnalyticsPerfTrendData.performance_trend_graph_points)

          this.goal_progress = tprepAnalyticsPerfTrendData.goal_progress
          this.accuracySeries = [tprepAnalyticsPerfTrendData.accuracy]
          this.avg_speed = tprepAnalyticsPerfTrendData.avg_speed > 0 ? (1 / tprepAnalyticsPerfTrendData.avg_speed).toFixed(3) : 0
          this.subject_wise_best_performance = reorderObjectBasedOnSetConfig(tprepAnalyticsPerfTrendData.subject_wise_best_performance, "app_name", this.$store)
        }
        this.isDataLoading = false
      }
      catch (err) {
        console.error("Exception in getTprepAnalyticsPerformanceTrend() and err: ", err.message)
      }
    },
    /**
     * tprep_analytics_get_attempted_apps_for_exam
     */
    async getTprepAnalyticsAttemptedAppsForExam () {
      try {
        const payload = {
          gide_user_id: this.gideUserId,
          tprep_mock_interest_id: this.interestId
        }

        const tprepAnalyticsAttemptedAppsForExamData = await TprepAnalytics.tprep_analyticsGetAttemptedAppsForExam(this, payload)
        if (tprepAnalyticsAttemptedAppsForExamData.resp_status) {
          this.attemptedAppsForExam = tprepAnalyticsAttemptedAppsForExamData.resp_data?.data

          if (this.selectedApp) {
            const selectedObj = this.attemptedAppsForExam.find(e => e.app_id == this.selectedApp || e.app_chain?.includes(this.selectedApp))
            this.selectedAppName = selectedObj?.subject_module_name ?? null
            if (selectedObj) {
              this.selectedApp = selectedObj.app_id
            }
          }
        }
        else {
          this.attemptedAppsForExam = []
        }
      }
      catch (err) {
        console.error("Exception in getTprepAnalyticsAttemptedAppsForExam() and err: ", err.message)
      }
    },
    /**
     * set Primary Color
     */
    setPrimaryColor () {
      // Get the root element's computed styles
      const rootElement = document.documentElement
      const computedStyles = getComputedStyle(rootElement)

      // Get the value of the CSS variable
      const primaryColor = computedStyles.getPropertyValue("--bs-primary").trim()

      // Update the data property with the computed color, with a fallback
      this.primaryColor = primaryColor || "#e5252c"

      // Update the chart options

      // score color
      this.performanceTrendOptions.title.style.color = this.primaryColor
      this.performanceTrendOptions.colors = [this.primaryColor]
      // accuracy color
      this.accuracyChartOptions.colors = [this.primaryColor]
      // readiness color
      this.readinessOptions = {
        ...this.readinessOptions,
        colors: [this.primaryColor]
      }
      // exam activity color
      this.examChartOptions.title.style.color = this.primaryColor
      this.examChartOptions.colors = [this.primaryColor]
      // attempt-wise module performance color
      this.stackedColumnOptions.title.style.color = this.primaryColor
    },
    /**
     * fix Test Names as per practice  or exam or drill
     */
    fixTestName (name = null) {
      if (name.includes(":")) {
        name = name.split(":")[1]
      }
      return name
    },
    /**
     * goToExam
    */
    goToExam () {
      this.$router.push(`/exams`)
    }
  }
}
</script>
<style scoped>
  .heading {
    font-weight: 750;
    font-size: 28px;
  }
  .box{
    border-radius: 8px;
    border: 1px solid var(--bs-secondary);
    padding: 12px;
    background-color: white;
    margin-bottom: 16px;
    height: 100%;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  .box-heading{
    padding-left: 8px;
    font-size: 22px;
    font-weight: 600;
    color: var(--bs-primary)
  }
  .subject-wise-box, .actionable-box{
     border: 1px solid var(--bs-secondary);
     /* margin-bottom: 16px; */
     width: 100%;
  }
  .actionable-box{
     border: 1px solid var(--bs-secondary);
     margin-bottom: 16px;
     width: 100%;
  }
  .h-27{
    height: 27px;
  }
  .insights-border{
    border: 1px solid var(--bs-secondary);
  }
  .image{
    width: 100%;
    max-width: 1300px;
    padding: 8px;
    object-fit: contain;
    border-radius: 8px;
    border: 1px solid var(--bs-secondary);
  }
  .max-700{
    max-width: 700px;
  }
  .upgrade-link{
    cursor: pointer;
  }
  .upgrade-link:hover {
    text-decoration: underline;
  }
  .spinner-container{
    height: 100dvh;
  }
  .spinner {
    width: 45px;
    height: 45px;
  }
  .chart-scroll-x {
    overflow-x: auto;
    overflow-y: hidden;
  }

  @media (min-width: 755px) {
    .subject-wise-box{
      max-width: 300px;
    }
  }
  @media (min-width: 768px) {
    .heading {
      font-size: 40px;
    }
  }
  @media (min-width: 1200px) {
    .heading {
      font-size: 48px;
    }
  }
  .topic-accuracy-container {
    height: 295px;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 6px;
  }
  .topic-accuracy-scroll {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    height: 100%;
    gap: 12px 20px;
    align-content: flex-start;
  }
  .g-bar-item {
    width: 100%;
    min-width: 250px;
    flex: 0 0 auto;
  }
  .topic-pct {
    color: var(--bs-primary);
  }
  .g-bar-item.weak .topic-pct {
    color: #e67e22;
  }
  .g-progress-track {
    display: block;
    height: 8px;
    border-radius: 99px;
    background: #f0f0f0;
    overflow: hidden;
    width: 100%;
  }
  .g-progress-fill {
    display: block;
    height: 100%;
    border-radius: 99px;
    background: var(--bs-primary);
    transition: width 0.8s ease;
  }
  .g-bar-item.weak .g-progress-fill {
    background: linear-gradient(90deg, #e74c3c, #e67e22);
  }
  .topic-accuracy-container::-webkit-scrollbar,
  .focus-strengths-container::-webkit-scrollbar {
    height: 6px;
    width: 6px;
  }
  .topic-accuracy-container::-webkit-scrollbar-track,
  .focus-strengths-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  .topic-accuracy-container::-webkit-scrollbar-thumb,
  .focus-strengths-container::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
  .topic-accuracy-container::-webkit-scrollbar-thumb:hover,
  .focus-strengths-container::-webkit-scrollbar-thumb:hover {
    background: #999;
  }
  .focus-strengths-container {
    height: 295px;
    overflow-y: auto;
    padding-right: 4px;
  }
  .g-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .g-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    font-weight: 600;
    padding: 6px 12px;
    border-radius: 99px;
    background: #fff;
    border: 1px solid #e0e0e0;
    transition: all 0.2s ease;
  }
  .g-chip.chip-good {
    color: #198754;
    border-color: rgba(25, 135, 84, 0.25);
    background: rgba(25, 135, 84, 0.08);
  }
  .g-chip.chip-weak {
    color: var(--bs-primary);
    border-color: rgba(229, 37, 44, 0.25);
    background: rgba(229, 37, 44, 0.08);
  }
  .readiness-text {
    font-size: 13px;
    color: #666;
    line-height: 1.5;
  }
</style>
